import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trophy, ChevronDown, Lightbulb } from 'lucide-react';
import ComprehensiveAnalysisResults from './ComprehensiveAnalysisResults';
import AIEnhancement from './AIEnhancement';

function BestOfThreeResults({ results, darkMode, user, authToken }) {
  const [activeTab, setActiveTab] = useState('winner');
  const [expandedImages, setExpandedImages] = useState({});

  const toggleImageExpansion = (index) => {
    setExpandedImages(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  if (!results || results.length === 0) return null;

  const userPurpose = results[0]?.user_purpose || 'Image comparison';

  return (
    <div className="space-y-6 mt-6">
      {/* User's Purpose Display */}
      {userPurpose && userPurpose !== 'Image comparison' && (
        <Card className={`${darkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'} border-2`}>
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <div className="font-semibold text-sm mb-1">Your Question:</div>
                <p className={`text-sm italic ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  "{userPurpose}"
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Winner Card */}
      <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border-2 border-yellow-400">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row items-start gap-4">
            <Trophy className="w-12 h-12 text-yellow-600 flex-shrink-0" />
            <div className="flex-1">
              <h4 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                🏆 Winner: Image #{results[0].imageIndex + 1}
              </h4>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                Best choice for: <span className="font-semibold">{userPurpose}</span>
              </p>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                Overall Score: <span className="font-bold text-lg text-green-600">{results[0].overallScore.toFixed(1)}/100</span>
              </p>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <div className="text-xs text-gray-600">Valence</div>
                  <div className="font-bold text-green-600">{((results[0].valence || 0) * 100).toFixed(0)}%</div>
                </div>
                <div>
                  <div className="text-xs text-gray-600">Arousal</div>
                  <div className="font-bold text-orange-600">{((results[0].arousal_level || 0) * 100).toFixed(0)}%</div>
                </div>
                <div>
                  <div className="text-xs text-gray-600">Dominance</div>
                  <div className="font-bold text-purple-600">{((results[0].dominance || 0) * 100).toFixed(0)}%</div>
                </div>
              </div>
              {results[0].custom_answer && (
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} mt-3 border-l-4 border-yellow-500`}>
                  <div className="font-semibold text-sm mb-2 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-yellow-600" />
                    Why this image wins:
                  </div>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed whitespace-pre-line`}>
                    {results[0].custom_answer}
                  </p>
                </div>
              )}
            </div>
            <img
              src={results[0].preview}
              alt="Winner"
              className="w-32 h-32 object-cover rounded-lg border-4 border-yellow-400"
            />
          </div>
        </CardContent>
      </Card>

      {/* Comprehensive Comparison Table */}
      <Card className={darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}>
        <CardHeader>
          <CardTitle className={darkMode ? 'text-white' : 'text-gray-900'}>
            Detailed Comparison
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Main Scores Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <th className="text-left p-2 font-semibold">Rank</th>
                  <th className="text-left p-2 font-semibold">Image</th>
                  <th className="text-left p-2 font-semibold">Overall</th>
                  <th className="text-left p-2 font-semibold">Valence</th>
                  <th className="text-left p-2 font-semibold">Arousal</th>
                  <th className="text-left p-2 font-semibold">Dominance</th>
                  <th className="text-left p-2 font-semibold">Emotion</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result, index) => (
                  <tr key={result.imageIndex} className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <td className="p-2">
                      <span className={`font-bold ${index === 0 ? 'text-yellow-600' : ''}`}>
                        {index === 0 ? '🏆 #1' : `#${index + 1}`}
                      </span>
                    </td>
                    <td className="p-2">
                      <img 
                        src={result.preview} 
                        alt={`Image ${result.imageIndex + 1}`} 
                        className="w-16 h-16 object-cover rounded border-2 border-gray-300"
                      />
                    </td>
                    <td className="p-2">
                      <span className="font-bold text-lg">{result.overallScore.toFixed(1)}</span>
                    </td>
                    <td className="p-2 text-green-600 font-semibold">
                      {((result.valence || 0) * 100).toFixed(0)}%
                    </td>
                    <td className="p-2 text-orange-600 font-semibold">
                      {((result.arousal_level || 0) * 100).toFixed(0)}%
                    </td>
                    <td className="p-2 text-purple-600 font-semibold">
                      {((result.dominance || 0) * 100).toFixed(0)}%
                    </td>
                    <td className="p-2 capitalize text-sm">
                      {result.dominant_emotion}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Top Emotions Comparison */}
          <div>
            <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Top Emotions by Image
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {results.map((result, index) => (
                <div key={result.imageIndex} className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="font-semibold mb-2 flex items-center gap-2">
                    <img src={result.preview} alt="" className="w-8 h-8 object-cover rounded" />
                    Image #{result.imageIndex + 1}
                    {index === 0 && <span className="text-yellow-600">👑</span>}
                  </div>
                  <div className="space-y-2 text-sm">
                    {Object.entries(result.emotions || {})
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 5)
                      .map(([emotion, score]) => (
                        <div key={emotion} className="flex justify-between items-center">
                          <span className="capitalize">{emotion}</span>
                          <span className="font-bold">{Math.round(score)}%</span>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Key Differences */}
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
            <h4 className="font-semibold mb-2">💡 Key Insights</h4>
            <ul className="space-y-1 text-sm">
              <li>• Winner (Image #{results[0].imageIndex + 1}) scored {results[0].overallScore.toFixed(1)} points</li>
              {results.length > 1 && (
                <li>• Difference from 2nd place: {(results[0].overallScore - results[1].overallScore).toFixed(1)} points</li>
              )}
              <li>• Dominant emotion: {results[0].dominant_emotion}</li>
              <li>• Highest valence (positivity): Image #{results.reduce((max, r) => r.valence > max.valence ? r : max).imageIndex + 1}</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Individual Image Details */}
      {results.map((result, index) => (
        <Card key={result.imageIndex} className={darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}>
          <CardHeader>
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => toggleImageExpansion(index)}
            >
              <div className="flex items-center gap-3">
                <img 
                  src={result.preview} 
                  alt={`Image ${result.imageIndex + 1}`}
                  className="w-12 h-12 object-cover rounded border-2 border-gray-300"
                />
                <div>
                  <CardTitle className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Image #{result.imageIndex + 1}
                    {index === 0 && <span className="ml-2 text-yellow-600">👑 Winner</span>}
                  </CardTitle>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Score: {result.overallScore.toFixed(1)}/100
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm">
                <ChevronDown className={`w-5 h-5 transition-transform ${expandedImages[index] ? 'rotate-180' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          
          {expandedImages[index] && (
            <CardContent className="space-y-6">
              {/* Full Analysis */}
              <ComprehensiveAnalysisResults
                result={result}
                darkMode={darkMode}
              />
              
              {/* AI Enhancement */}
              <AIEnhancement
                analysis={result}
                darkMode={darkMode}
                user={user}
                authToken={authToken}
              />
            </CardContent>
          )}
        </Card>
      ))}

      {/* Start New Comparison Button */}
      <div className="text-center pt-4">
        <Button
          onClick={() => {
            console.log('🚀 FINAL FIX: Start New Comparison - using location.replace');
            // Nuclear option: location.replace for complete reset
            window.location.replace('/?clear=' + Date.now());
          }}
          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
        >
          Start New Comparison
        </Button>
      </div>
    </div>
  );
}

export default BestOfThreeResults;
